import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useAuth } from '../../contexts/AuthContext';
import { apiService, ApiError } from '../../services/api';
import { FuelCreditEntry, FuelCreditsHistoryResponse, ReferralPairingEntry, ReferralStats } from '../../types/api';
import {
  Loader2,
  Users,
  Fuel,
  TrendingUp,
  Clock,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Copy,
  Check,
  Zap,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const PAGE_SIZE = 20;

function sourceBadge(source: FuelCreditEntry['source']) {
  const map: Record<FuelCreditEntry['source'], string> = {
    referral: 'bg-green-100 text-green-700',
    admin: 'bg-blue-100 text-blue-700',
    purchase: 'bg-purple-100 text-purple-700',
    debit: 'bg-red-100 text-red-700',
    action: 'bg-orange-100 text-orange-700',
  };
  return map[source] ?? 'bg-gray-100 text-gray-700';
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface StatCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  sub?: string;
  onClick?: () => void;
}

function StatCard({ label, value, icon, sub, onClick }: StatCardProps) {
  return (
    <Card
      className={onClick ? 'cursor-pointer hover:bg-muted/40 transition-colors' : ''}
      onClick={onClick}
    >
      <CardContent className="p-4 flex items-start gap-3">
        <div className="mt-0.5 text-muted-foreground">{icon}</div>
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className={`text-2xl font-semibold ${onClick ? 'underline decoration-dotted underline-offset-2' : ''}`}>{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export function ReferralV1({ isActive }: { isActive?: boolean }) {
  const { user } = useAuth();

  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [history, setHistory] = useState<FuelCreditsHistoryResponse | null>(null);
  const [pairings, setPairings] = useState<ReferralPairingEntry[] | null>(null);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingCode, setLoadingCode] = useState(false);
  const [loadingPairings, setLoadingPairings] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [simulateResult, setSimulateResult] = useState<string | null>(null);
  const [simulatingTrial, setSimulatingTrial] = useState(false);
  const [simulateTrialResult, setSimulateTrialResult] = useState<string | null>(null);

  const managerSession = localStorage.getItem('managerSession');
  const managerRole = managerSession ? (JSON.parse(managerSession)?.role || 'manager') : 'manager';
  const isAdmin = managerRole === 'admin';
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  // null = dialog closed, 'all' = show all, specific status = filtered
  const [dialogFilter, setDialogFilter] = useState<'all' | 'awaiting_otp' | 'pending' | 'linked' | null>(null);

  const hasFetchedStats = useRef(false);

  const fetchStats = useCallback(async () => {
    if (!user?.id) return;
    setLoadingStats(true);
    setError('');
    try {
      const data = await apiService.getReferralStats(user.id);
      setStats(data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load referral stats');
    } finally {
      setLoadingStats(false);
    }
  }, [user?.id]);

  const fetchHistory = useCallback(async (pageIndex: number) => {
    if (!user?.id) return;
    setLoadingHistory(true);
    setError('');
    try {
      const data = await apiService.getFuelCreditsHistory(user.id, PAGE_SIZE, pageIndex * PAGE_SIZE);
      setHistory(data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load fuel credit history');
    } finally {
      setLoadingHistory(false);
    }
  }, [user?.id]);

  const fetchPairings = useCallback(async () => {
    if (!user?.id) return;
    setLoadingPairings(true);
    try {
      const data = await apiService.getReferralPairings(user.id);
      setPairings(data.pairings);
    } catch {
      // non-critical
    } finally {
      setLoadingPairings(false);
    }
  }, [user?.id]);

  const fetchCode = useCallback(async () => {
    if (!user?.id) return;
    setLoadingCode(true);
    try {
      const data = await apiService.getReferralCode(user.id);
      setInviteUrl(data.invite_url);
    } catch {
      // non-critical — invite URL section will just stay hidden
    } finally {
      setLoadingCode(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!isActive) return;
    if (hasFetchedStats.current) return;
    hasFetchedStats.current = true;
    fetchStats();
    fetchHistory(0);
    fetchCode();
    fetchPairings();
  }, [isActive, fetchStats, fetchHistory, fetchCode, fetchPairings]);

  const handleCopyInviteUrl = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = inviteUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const [claimingId, setClaimingId] = useState<string | null>(null);

  const handleSimulatePurchase = async () => {
    if (!user?.id) return;
    setSimulating(true);
    setSimulateResult(null);
    try {
      const res = await apiService.simulateReferralPurchase(user.id);
      setSimulateResult(res.message);
      fetchStats();
      fetchHistory(page);
    } catch (err) {
      setSimulateResult(err instanceof ApiError ? err.message : 'Simulation failed');
    } finally {
      setSimulating(false);
    }
  };

  const handleSimulateTrialPurchase = async () => {
    if (!user?.id) return;
    setSimulatingTrial(true);
    setSimulateTrialResult(null);
    try {
      const res = await apiService.simulateTrialPurchase(user.id);
      const rewardMsg = res.referralRewardActivated
        ? 'Referral reward was activated'
        : 'No referral reward found';
      setSimulateTrialResult(`${res.message} · ${rewardMsg}`);
      fetchStats();
      fetchHistory(page);
    } catch (err) {
      setSimulateTrialResult(err instanceof ApiError ? err.message : 'Simulation failed');
    } finally {
      setSimulatingTrial(false);
    }
  };

  const handleClaim = async (entryId: string) => {
    setClaimingId(entryId);
    try {
      await apiService.claimFuelCredit(entryId);
      fetchStats();
      fetchHistory(page);
    } catch {
      // non-critical
    } finally {
      setClaimingId(null);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchHistory(newPage);
  };

  const totalPages = history ? Math.ceil(history.total / PAGE_SIZE) : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">Referral &amp; Fuel Credits</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Referral program stats and credit ledger</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSimulatePurchase}
              disabled={simulating}
              className="flex items-center gap-1.5 text-orange-700 border-orange-300 hover:bg-orange-50"
            >
              {simulating ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Zap className="h-3.5 w-3.5" />
              )}
              Simulate Purchase
            </Button>
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSimulateTrialPurchase}
                disabled={simulatingTrial}
                className="flex items-center gap-1.5 text-blue-700 border-blue-300 hover:bg-blue-50"
              >
                {simulatingTrial ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Zap className="h-3.5 w-3.5" />
                )}
                Simulate Trial Purchase
              </Button>
            )}
          </div>
          {simulateResult && (
            <p className="text-xs text-muted-foreground max-w-[280px] text-right">{simulateResult}</p>
          )}
          {simulateTrialResult && (
            <p className="text-xs text-muted-foreground max-w-[280px] text-right">{simulateTrialResult}</p>
          )}
        </div>
      </div>

      {/* Invite URL */}
      <Card>
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground mb-1.5">Invite URL</p>
          {loadingCode ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Loading…</span>
            </div>
          ) : inviteUrl ? (
            <div className="flex items-center gap-2">
              <span className="text-sm font-mono text-foreground break-all flex-1">{inviteUrl}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 flex-shrink-0"
                onClick={handleCopyInviteUrl}
                title="Copy invite URL"
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-green-600" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">No referral code yet</span>
          )}
        </CardContent>
      </Card>

      {error && (
        <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
          {error}
        </div>
      )}

      {/* Stats grid */}
      {loadingStats ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : stats ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <StatCard
            label="Fuel Credits Balance"
            value={stats.fuel_credits_balance}
            icon={<Fuel className="h-5 w-5" />}
          />
          <StatCard
            label="Total Referrals"
            value={stats.count}
            icon={<Users className="h-5 w-5" />}
            sub={`${stats.cap_remaining} slots remaining`}
            onClick={() => setDialogFilter('all')}
          />
          <StatCard
            label="Completed"
            value={stats.completed}
            icon={<CheckCircle className="h-5 w-5" />}
            onClick={() => setDialogFilter('linked')}
          />
          <StatCard
            label="Pending"
            value={stats.pending}
            icon={<Clock className="h-5 w-5" />}
            onClick={() => setDialogFilter('pending')}
          />
          <StatCard
            label="Rewards Earned"
            value={stats.rewards_earned}
            icon={<TrendingUp className="h-5 w-5" />}
          />
        </div>
      ) : null}

      {/* History table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Credit Ledger</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loadingHistory ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : history && history.entries.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="px-4 py-2 font-medium">Date</th>
                      <th className="px-4 py-2 font-medium">Amount</th>
                      <th className="px-4 py-2 font-medium">Source</th>
                      <th className="px-4 py-2 font-medium">Note</th>
                      <th className="px-4 py-2 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.entries.map((entry) => (
                      <tr key={entry.id} className="border-b last:border-0 hover:bg-muted/40 transition-colors">
                        <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                          {formatDate(entry.created_at)}
                        </td>
                        <td className={`px-4 py-2 font-medium tabular-nums ${entry.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {entry.amount >= 0 ? '+' : ''}{entry.amount}
                          {entry.status === 'unclaimed' && (
                            <span className="ml-1.5 text-xs font-normal text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">Ready to claim</span>
                          )}
                          {entry.status === 'awaiting_trial_purchase' && (
                            <span className="ml-1.5 text-xs font-normal text-gray-500 bg-gray-50 border border-gray-200 rounded-full px-1.5 py-0.5">Pending</span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${sourceBadge(entry.source)}`}>
                            {entry.source}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-muted-foreground max-w-[200px] truncate">
                          {entry.status === 'awaiting_trial_purchase'
                            ? 'Pending — waiting for referee\'s 30-day trial'
                            : (entry.note ?? '—')}
                        </td>
                        <td className="px-4 py-2">
                          {entry.status === 'unclaimed' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-6 px-2 text-xs"
                              disabled={claimingId === entry.id}
                              onClick={() => handleClaim(entry.id)}
                            >
                              {claimingId === entry.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                'Claim'
                              )}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-xs text-muted-foreground">
                    {history.offset + 1}–{Math.min(history.offset + history.entries.length, history.total)} of {history.total}
                  </p>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      disabled={page === 0}
                      onClick={() => handlePageChange(page - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-xs text-muted-foreground px-1">
                      {page + 1} / {totalPages}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      disabled={page >= totalPages - 1}
                      onClick={() => handlePageChange(page + 1)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            !loadingHistory && (
              <div className="text-center py-10 text-muted-foreground text-sm">
                No credit history found
              </div>
            )
          )}
        </CardContent>
      </Card>

      {/* Pairings detail dialog */}
      <Dialog open={dialogFilter !== null} onOpenChange={(open) => { if (!open) setDialogFilter(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {dialogFilter === 'all' && 'All Referrals'}
              {dialogFilter === 'pending' && 'Pending Referrals'}
              {dialogFilter === 'linked' && 'Completed Referrals'}
              {dialogFilter === 'awaiting_otp' && 'Awaiting OTP'}
            </DialogTitle>
          </DialogHeader>

          {loadingPairings ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (() => {
            const filtered = (pairings ?? []).filter(
              (p) => dialogFilter === 'all' || p.status === dialogFilter,
            );
            if (filtered.length === 0) {
              return (
                <p className="text-sm text-muted-foreground text-center py-8">No entries</p>
              );
            }
            return (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="px-3 py-2 font-medium">Phone</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 font-medium">User ID</th>
                      <th className="px-3 py-2 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((p) => (
                      <tr key={p.id} className="border-b last:border-0 hover:bg-muted/40 transition-colors">
                        <td className="px-3 py-2 font-mono">{p.phone_masked}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                            p.status === 'linked'
                              ? 'bg-green-100 text-green-700'
                              : p.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {p.status === 'awaiting_otp' ? 'awaiting otp' : p.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground font-mono text-xs">
                          {p.referee_user_id ? p.referee_user_id.slice(0, 8) + '…' : '—'}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                          {formatDate(p.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
